import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const SOURCE_CHIP = {
  target_completion: "bg-teal-50 border-teal-600/30 text-teal-800",
  certifications_earned: "bg-teal-50 border-teal-600/30 text-teal-800",
  skill_readiness_delta: "bg-teal-50 border-teal-600/30 text-teal-800",
  compliance_status: "bg-teal-50 border-teal-600/30 text-teal-800",
  peer_collaboration: "bg-teal-50 border-teal-600/30 text-teal-800",
  manual: "bg-slate-100 border-slate-300 text-slate-600",
};

export const MatrixDetail = ({ matrixId, onBack }) => {
  const { user } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [weights, setWeights] = useState({});
  const [busy, setBusy] = useState(false);
  const canManage = ["admin", "hr_manager"].includes(user.role);

  const load = () => api.get(`/kpi/matrices/${matrixId}`).then((r) => {
    setMatrix(r.data);
    setWeights(Object.fromEntries(r.data.kpis.map((k) => [k.key, k.weight_pct])));
  });
  useEffect(() => { load(); }, [matrixId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!matrix) return <Skeleton className="h-96 w-full rounded-none" />;

  const total = Object.values(weights).reduce((a, b) => a + Number(b || 0), 0);
  const dirty = matrix.kpis.some((k) => Number(weights[k.key]) !== k.weight_pct);

  const saveWeights = async () => {
    setBusy(true);
    try {
      const kpis = matrix.kpis.map((k) => ({ ...k, weight_pct: Number(weights[k.key]) }));
      await api.patch(`/kpi/matrices/${matrixId}`, { kpis });
      toast.success("Matrix weights updated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/kpi/matrices/${matrixId}/approve`);
      toast.success(`Matrix activated — ${r.data.instances_created} employee instance(s) created`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="matrix-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} data-testid="matrix-back-btn"
            className="rounded-none h-8 text-[12px] text-muted-foreground -ml-2">
            <ArrowLeft className="h-4 w-4" /> Roles
          </Button>
          <h2 className="text-lg font-bold text-[var(--brand-primary)]">{matrix.role_title}</h2>
          <span className="text-[11px] font-mono text-muted-foreground">{matrix.department} · {matrix.seniority}</span>
          <span className={`text-[9px] font-mono px-1.5 py-0.5 uppercase ${matrix.status === "active" ? "bg-teal-600 text-white" : "border border-amber-500 text-amber-700"}`}
            data-testid="matrix-status-chip">
            {matrix.status.replace("_", " ")}
          </span>
        </div>
        {canManage && matrix.status === "ai_draft" && (
          <Button onClick={approve} disabled={busy} data-testid="matrix-approve-btn"
            className="rounded-none h-9 text-[12px] bg-teal-600 hover:bg-teal-700">
            <CheckCircle2 className="h-4 w-4" /> Approve & activate
          </Button>
        )}
      </div>

      <div className="bg-[var(--brand-primary)] text-white px-4 py-2.5 flex items-center gap-2 flex-wrap" data-testid="matrix-provenance">
        <ShieldCheck className="h-3.5 w-3.5 text-teal-400" />
        <span className="text-[11px] font-mono text-slate-300">
          Generated by {matrix.provenance?.generated_by} · {matrix.provenance?.model} · gateway audit{" "}
          {matrix.provenance?.audit_id} · requested by {matrix.provenance?.requested_by} ·{" "}
          {String(matrix.provenance?.generated_at).slice(0, 10)}
          {matrix.provenance?.approved_note ? ` · ${matrix.provenance.approved_note}` : ""}
        </span>
      </div>

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-[12.5px] min-w-[860px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-slate-50">
              {["KPI", "Description", "Weight %", "Target", "Threshold", "Source", "Cadence"].map((h) => (
                <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {matrix.kpis.map((k) => (
              <tr key={k.key} data-testid={`matrix-kpi-${k.key}`}>
                <td className="px-4 py-3 font-medium text-[var(--brand-primary)] whitespace-nowrap">{k.name}</td>
                <td className="px-4 py-3 text-slate-600 text-[12px] max-w-[340px]">{k.description}</td>
                <td className="px-4 py-3">
                  {canManage ? (
                    <Input type="number" step="0.5" value={weights[k.key] ?? ""} data-testid={`weight-input-${k.key}`}
                      onChange={(e) => setWeights({ ...weights, [k.key]: e.target.value })}
                      className="rounded-none w-20 h-8 font-mono text-right" />
                  ) : <span className="font-mono">{k.weight_pct}</span>}
                </td>
                <td className="px-4 py-3 font-mono">{k.target} {k.unit}</td>
                <td className="px-4 py-3 font-mono text-slate-500">{k.threshold}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono border px-1.5 py-0.5 ${SOURCE_CHIP[k.source]}`}>{k.source}</span>
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-500">{k.cadence}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-border flex items-center justify-between">
          <p className={`text-[12px] font-mono ${Math.round(total * 10) / 10 === 100 ? "text-teal-700" : "text-red-600 font-bold"}`}
            data-testid="weights-total">
            Weights total: {Math.round(total * 10) / 10}% {Math.round(total * 10) / 10 !== 100 && "— must equal 100"}
          </p>
          {canManage && dirty && (
            <Button onClick={saveWeights} disabled={busy || Math.round(total * 10) / 10 !== 100}
              data-testid="matrix-save-btn" className="rounded-none h-8 text-[12px] bg-teal-600 hover:bg-teal-700">
              Save weights
            </Button>
          )}
        </div>
      </div>

      {(matrix.edit_history || []).length > 0 && (
        <p className="text-[11px] font-mono text-muted-foreground">
          Edits: {matrix.edit_history.map((h) => `${h.by} ${String(h.at).slice(0, 10)}`).join(" · ")}
        </p>
      )}
    </div>
  );
};

export default MatrixDetail;
